// Account-level settings: the LLM connection(s) and API tokens.
//
// Search options (strategy, depth, IOC/web search, experimental switches)
// are per-question and live in the chat input's options popover, stored in
// this browser. This page never reads or writes them.
import { useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useBlocker, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { fetchAvailableModels } from '../../lib/models';
import { fetchSettings, saveSettings } from '../../lib/api';
import { useAuth } from '../auth/useAuth';
import ApiTokensSection from './ApiTokensSection';
import type { ServerSettings } from '../../types';

// Legacy: before server-side settings, the LLM config (incl. API keys)
// lived in the chat options in localStorage.
const OPTIONS_KEY = 'malpedia-chat-options';

type Backend = 'openai' | 'anthropic' | 'openai_compatible' | 'fhgenie';

const BASE_BACKENDS: Backend[] = ['openai', 'anthropic', 'openai_compatible'];

const FHGENIE_URLS = {
  default: 'https://fhgenie.fraunhofer.de/v1',
  preview: 'https://fhgenie-preview.c.fraunhofer.de/v1',
} as const;

const FHGENIE_URL_VALUES: string[] = Object.values(FHGENIE_URLS);

const BASE_URL_PLACEHOLDERS: Record<Backend, string> = {
  openai: 'https://api.openai.com/v1',
  anthropic: 'https://api.anthropic.com',
  openai_compatible: 'http://localhost:8080/v1',
  fhgenie: FHGENIE_URLS.default,
};

const BACKEND_LABELS: Record<Backend, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  openai_compatible: 'OpenAI-compatible',
  fhgenie: 'FHGenie',
};

interface LlmConfig {
  backend: Backend;
  baseUrl: string;
  model: string;
}

interface FormState {
  main: LlmConfig;
  planner: LlmConfig;
  usePlanner: boolean;
  // Typed-in keys only; stored keys are never sent back to the browser.
  mainKey: string;
  plannerKey: string;
}

interface KeyState {
  stored: boolean;
  hint: string;
}

function asBackend(value: unknown, fallback: Backend = 'openai'): Backend {
  return typeof value === 'string' && value in BACKEND_LABELS ? (value as Backend) : fallback;
}

function withFhgenieDefault(cfg: LlmConfig): LlmConfig {
  return cfg.backend === 'fhgenie' && !cfg.baseUrl ? { ...cfg, baseUrl: FHGENIE_URLS.default } : cfg;
}

function formFromServer(s: ServerSettings): FormState {
  const main = withFhgenieDefault({
    backend: asBackend(s.llmBackend),
    baseUrl: s.llmBaseUrl || '',
    model: s.llmModel || '',
  });
  const usePlanner = !!(
    (s.plannerLlmModel && s.plannerLlmModel !== s.llmModel) ||
    (s.plannerLlmBackend && s.plannerLlmBackend !== s.llmBackend)
  );
  const planner = usePlanner
    ? withFhgenieDefault({
        backend: asBackend(s.plannerLlmBackend),
        baseUrl: s.plannerLlmBaseUrl || '',
        model: s.plannerLlmModel || '',
      })
    : main;
  return { main, planner, usePlanner, mainKey: '', plannerKey: '' };
}

function readLegacyOptions(): Record<string, unknown> {
  try {
    const raw = localStorage.getItem(OPTIONS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

/** Drop API keys left in localStorage by the pre-server-settings client. */
function stripLegacyKeys() {
  try {
    const legacy = readLegacyOptions();
    if (!('llmApiKey' in legacy) && !('plannerLlmApiKey' in legacy)) return;
    delete legacy.llmApiKey;
    delete legacy.plannerLlmApiKey;
    localStorage.setItem(OPTIONS_KEY, JSON.stringify(legacy));
  } catch {
    /* ignore */
  }
}

/** First visit (no server row): seed from the legacy browser config. */
function formFromLegacy(isFKIE: boolean): FormState {
  const legacy = readLegacyOptions();
  let main: LlmConfig = {
    backend: asBackend(legacy.llmBackend, isFKIE ? 'fhgenie' : 'openai'),
    baseUrl: typeof legacy.llmBaseUrl === 'string' ? legacy.llmBaseUrl : '',
    model: typeof legacy.llmModel === 'string' ? legacy.llmModel : '',
  };
  if (isFKIE && !legacy.llmBackend) {
    main = { backend: 'fhgenie', baseUrl: FHGENIE_URLS.default, model: main.model || 'MiniMaxAI/MiniMax-M2.5' };
  }
  main = withFhgenieDefault(main);
  return {
    main,
    planner: main,
    usePlanner: false,
    mainKey: typeof legacy.llmApiKey === 'string' ? legacy.llmApiKey : '',
    plannerKey: '',
  };
}

function sameForm(a: FormState, b: FormState): boolean {
  // The planner fields are irrelevant while the planner toggle is off.
  const norm = (f: FormState) => (f.usePlanner ? f : { ...f, planner: null, plannerKey: '' });
  return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
}

// ── Small layout pieces ──────────────────────────────────────────────────

function Field({ label, help, children }: { label: string; help?: ReactNode; children: ReactNode }) {
  return (
    <div className="malpedia-settings-field">
      <div className="malpedia-settings-field-text">
        <span className="malpedia-settings-field-label">{label}</span>
        {help && <span className="malpedia-settings-field-help">{help}</span>}
      </div>
      <div className="malpedia-settings-field-control">{children}</div>
    </div>
  );
}

function Segmented<T extends string>({
  value,
  options,
  onChange,
  disabled,
}: {
  value: T;
  options: { value: T; label: string; title?: string }[];
  onChange: (v: T) => void;
  disabled?: boolean;
}) {
  return (
    <div className="malpedia-settings-segmented" role="radiogroup">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          className={`malpedia-settings-segment${value === o.value ? ' active' : ''}`}
          onClick={() => onChange(o.value)}
          title={o.title}
          disabled={disabled}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** Combo input: text field + "Load models" button with filterable suggestions. */
function ModelSelect({
  backend,
  baseUrl,
  value,
  onChange,
  apiKey,
  hasServerKey,
}: {
  backend: string;
  baseUrl: string;
  value: string;
  onChange: (model: string) => void;
  apiKey?: string;
  hasServerKey?: boolean;
}) {
  const [models, setModels] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [fetchError, setFetchError] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);

  // Clear suggestions when backend or baseUrl changes
  useEffect(() => {
    setModels([]);
    setFetchError(false);
    setShowSuggestions(false);
  }, [backend, baseUrl]);

  // Click outside to close suggestions
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setShowSuggestions(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleFetch = useCallback(async () => {
    setLoading(true);
    setFetchError(false);
    try {
      const result = await fetchAvailableModels(backend, baseUrl, apiKey || undefined);
      setModels(result);
      setShowSuggestions(result.length > 0);
      if (result.length === 0) setFetchError(true);
    } catch {
      setModels([]);
      setFetchError(true);
    } finally {
      setLoading(false);
    }
  }, [backend, baseUrl, apiKey]);

  const filtered = models.filter((m) => m.toLowerCase().includes(value.toLowerCase()));
  const canFetch = !!(apiKey || hasServerKey);

  return (
    <div className="malpedia-settings-model-combo" ref={wrapRef}>
      <input
        type="text"
        className="malpedia-settings-input"
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
          if (models.length > 0) setShowSuggestions(true);
        }}
        onFocus={() => {
          if (models.length > 0) setShowSuggestions(true);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setShowSuggestions(false);
        }}
        placeholder="e.g. gpt-4o"
      />
      <button
        type="button"
        className="malpedia-settings-fetch-btn"
        onClick={handleFetch}
        disabled={loading}
        title={canFetch ? 'Load available models' : 'Load available models (most providers need an API key first)'}
      >
        {loading ? <span className="malpedia-settings-spinner" /> : '↻'}
      </button>

      {fetchError && models.length === 0 && !loading && (
        <div className="malpedia-settings-model-empty">No models found — check the URL and key</div>
      )}

      {showSuggestions && filtered.length > 0 && (
        <div className="malpedia-settings-model-suggestions">
          {filtered.map((m) => (
            <button
              key={m}
              type="button"
              className="malpedia-settings-model-suggestion"
              onClick={() => {
                onChange(m);
                setShowSuggestions(false);
              }}
            >
              {m}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/** Backend / URL / model / key fields — shared by the answer and planner LLM. */
function LlmFields({
  cfg,
  onChange,
  apiKey,
  onApiKeyChange,
  keyState,
  onClearKey,
  backends,
  locked,
}: {
  cfg: LlmConfig;
  onChange: (cfg: LlmConfig) => void;
  apiKey: string;
  onApiKeyChange: (v: string) => void;
  keyState: KeyState;
  onClearKey: () => void;
  backends: Backend[];
  locked: boolean;
}) {
  const isFhgenie = cfg.backend === 'fhgenie';

  const changeBackend = (backend: Backend) => {
    // Swap in/out the FHGenie URL so it never leaks into another backend.
    let baseUrl = cfg.baseUrl;
    if (backend === 'fhgenie' && !FHGENIE_URL_VALUES.includes(baseUrl)) baseUrl = FHGENIE_URLS.default;
    if (backend !== 'fhgenie' && FHGENIE_URL_VALUES.includes(baseUrl)) baseUrl = '';
    onChange({ ...cfg, backend, baseUrl });
  };

  return (
    <>
      <Field label="Provider">
        <select
          className="malpedia-settings-select"
          value={cfg.backend}
          onChange={(e) => changeBackend(e.target.value as Backend)}
          disabled={locked}
        >
          {backends.map((b) => (
            <option key={b} value={b}>{BACKEND_LABELS[b]}</option>
          ))}
        </select>
      </Field>

      {isFhgenie ? (
        <Field label="Gateway" help={cfg.baseUrl}>
          <Segmented
            value={cfg.baseUrl === FHGENIE_URLS.preview ? 'preview' : 'default'}
            options={[
              { value: 'default', label: 'Default', title: FHGENIE_URLS.default },
              { value: 'preview', label: 'Preview', title: FHGENIE_URLS.preview },
            ]}
            onChange={(v) => onChange({ ...cfg, baseUrl: FHGENIE_URLS[v] })}
            disabled={locked}
          />
        </Field>
      ) : (
        <Field label="Base URL" help="Leave empty for the provider's default endpoint">
          <input
            type="text"
            className="malpedia-settings-input"
            value={cfg.baseUrl}
            onChange={(e) => onChange({ ...cfg, baseUrl: e.target.value })}
            placeholder={BASE_URL_PLACEHOLDERS[cfg.backend]}
            disabled={locked}
          />
        </Field>
      )}

      <Field label="API key" help={keyState.stored ? 'A key is saved. Type a new one to replace it.' : undefined}>
        <div className="malpedia-settings-password-wrap">
          <input
            type="password"
            className="malpedia-settings-input"
            value={locked ? '' : apiKey}
            onChange={(e) => onApiKeyChange(e.target.value)}
            placeholder={locked ? 'Managed by an administrator' : keyState.stored ? `${keyState.hint} (saved)` : 'sk-...'}
            autoComplete="off"
            disabled={locked}
          />
          {keyState.stored && !locked && (
            <button
              type="button"
              className="malpedia-settings-clear-btn"
              onClick={onClearKey}
              title="Delete the saved API key"
            >
              Remove
            </button>
          )}
        </div>
      </Field>

      <Field label="Model" help={locked ? undefined : 'Type a model id, or load the list with ↻'}>
        {locked ? (
          <input type="text" className="malpedia-settings-input" value={cfg.model} disabled />
        ) : (
          <ModelSelect
            backend={cfg.backend}
            baseUrl={cfg.baseUrl}
            value={cfg.model}
            onChange={(model) => onChange({ ...cfg, model })}
            apiKey={apiKey}
            hasServerKey={keyState.stored}
          />
        )}
      </Field>
    </>
  );
}

// ── Page ─────────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: user } = useAuth();
  const isFKIE = user?.groups?.includes('FKIE') ?? false;
  const backends: Backend[] = isFKIE ? ['fhgenie', ...BASE_BACKENDS] : BASE_BACKENDS;

  const { data: serverSettings, isLoading: serverLoading } = useQuery({
    queryKey: ['settings'],
    queryFn: fetchSettings,
    staleTime: 60_000,
  });

  const [form, setForm] = useState<FormState | null>(null);
  const [saved, setSaved] = useState<FormState | null>(null);
  const [saving, setSaving] = useState(false);
  const [mainKeyState, setMainKeyState] = useState<KeyState>({ stored: false, hint: '' });
  const [plannerKeyState, setPlannerKeyState] = useState<KeyState>({ stored: false, hint: '' });
  const [migrating, setMigrating] = useState(false);

  const applyServer = useCallback((s: ServerSettings) => {
    setMainKeyState({ stored: s.hasLlmApiKey, hint: s.llmApiKeyHint || '' });
    setPlannerKeyState({ stored: s.hasPlannerLlmApiKey, hint: s.plannerLlmApiKeyHint || '' });
  }, []);

  // Initialize once the server answered (null = user never saved settings).
  const initialized = useRef(false);
  useEffect(() => {
    if (serverLoading || initialized.current) return;
    initialized.current = true;
    if (serverSettings) {
      const f = formFromServer(serverSettings);
      setForm(f);
      setSaved(f);
      applyServer(serverSettings);
    } else {
      const f = formFromLegacy(isFKIE);
      setForm(f);
      // Baseline without the legacy key, so a key to migrate shows as unsaved.
      setSaved({ ...f, mainKey: '' });
      setMigrating(!!f.mainKey);
    }
  }, [serverSettings, serverLoading, isFKIE, applyServer]);

  const isTestAccount = serverSettings?.isTestAccount ?? false;
  const dirty = !!form && !!saved && !isTestAccount && !sameForm(form, saved);

  // Warn before leaving with unsaved changes.
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) => dirty && currentLocation.pathname !== nextLocation.pathname,
  );
  useEffect(() => {
    if (blocker.state !== 'blocked') return;
    if (window.confirm('You have unsaved LLM settings. Leave without saving?')) blocker.proceed();
    else blocker.reset();
  }, [blocker]);
  useEffect(() => {
    if (!dirty) return;
    const handler = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const update = useCallback((partial: Partial<FormState>) => {
    setForm((prev) => (prev ? { ...prev, ...partial } : prev));
  }, []);

  const afterServerChange = useCallback(
    (result: ServerSettings) => {
      applyServer(result);
      queryClient.setQueryData(['settings'], result);
    },
    [applyServer, queryClient],
  );

  const handleSave = useCallback(async () => {
    if (!form) return;
    setSaving(true);
    try {
      const planner = form.usePlanner ? form.planner : { backend: '', model: '', baseUrl: '' };
      const body: Record<string, unknown> = {
        llmBackend: form.main.backend,
        llmModel: form.main.model,
        llmBaseUrl: form.main.baseUrl,
        // Empty planner fields = the server falls back to the answer model.
        plannerLlmBackend: planner.backend,
        plannerLlmModel: planner.model,
        plannerLlmBaseUrl: planner.baseUrl,
      };
      if (form.mainKey) body.llmApiKey = form.mainKey;
      if (!form.usePlanner) body.plannerLlmApiKey = '';
      else if (form.plannerKey) body.plannerLlmApiKey = form.plannerKey;

      const result = await saveSettings(body);
      afterServerChange(result);
      const next = { ...form, mainKey: '', plannerKey: '' };
      if (!next.usePlanner) next.planner = next.main;
      setForm(next);
      setSaved(next);
      if (migrating) {
        stripLegacyKeys();
        setMigrating(false);
      }
      toast.success('Settings saved');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  }, [form, migrating, afterServerChange]);

  const handleClearKey = useCallback(
    async (field: 'llmApiKey' | 'plannerLlmApiKey') => {
      if (!window.confirm('Delete the saved API key?')) return;
      setSaving(true);
      try {
        afterServerChange(await saveSettings({ [field]: '' }));
        toast.success('API key removed');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to remove key');
      } finally {
        setSaving(false);
      }
    },
    [afterServerChange],
  );

  if (serverLoading || !form) {
    return (
      <div className="malpedia-settings-page">
        <div className="malpedia-settings-container">
          <h1 className="malpedia-settings-title">Settings</h1>
          <p className="malpedia-settings-hint">Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="malpedia-settings-page malpedia-settings-page--cards">
      <div className="malpedia-settings-container">
        <header className="malpedia-settings-header">
          <h1 className="malpedia-settings-title">Settings</h1>
          <p className="malpedia-settings-subtitle">
            The language model that answers your questions, and tokens for API access.
          </p>
        </header>

        <div className="malpedia-settings-callout">
          <span className="malpedia-settings-callout-icon" aria-hidden="true">i</span>
          <span>
            Looking for search options like depth, IOC search or web search? They are set per
            question with the <strong>options button</strong> next to the chat input.
          </span>
          <button type="button" className="malpedia-settings-link" onClick={() => navigate('/chat')}>
            Open chat →
          </button>
        </div>

        {migrating && (
          <div className="malpedia-settings-callout warn">
            <span className="malpedia-settings-callout-icon" aria-hidden="true">!</span>
            <span>
              An API key from an older version is stored in this browser. <strong>Save</strong> to
              move it to your account. It is then removed from the browser.
            </span>
          </div>
        )}

        {isTestAccount && (
          <div className="malpedia-settings-callout">
            <span className="malpedia-settings-callout-icon" aria-hidden="true">i</span>
            <span>This is a test account. Its language model is managed by an administrator.</span>
          </div>
        )}

        <section className="malpedia-settings-card">
          <div className="malpedia-settings-card-head">
            <h2 className="malpedia-settings-section-title">Answer model</h2>
            <p className="malpedia-settings-hint">Writes the answers in every chat.</p>
          </div>
          <LlmFields
            cfg={form.main}
            onChange={(main) => update({ main })}
            apiKey={form.mainKey}
            onApiKeyChange={(mainKey) => update({ mainKey })}
            keyState={mainKeyState}
            onClearKey={() => handleClearKey('llmApiKey')}
            backends={backends}
            locked={isTestAccount}
          />
        </section>

        {!isTestAccount && (
          <section className="malpedia-settings-card">
            <div className="malpedia-settings-card-head malpedia-settings-card-head--toggle">
              <div>
                <h2 className="malpedia-settings-section-title">Separate planner model</h2>
                <p className="malpedia-settings-hint">
                  The planner reads your question and decides which searches to run (Auto and
                  Planned strategies). When off, the answer model does this too.
                </p>
              </div>
              <label className="malpedia-settings-toggle" title="Use a different model for planning">
                <input
                  type="checkbox"
                  checked={form.usePlanner}
                  onChange={(e) => {
                    const on = e.target.checked;
                    // Start from the answer model so only the differences need editing.
                    update(on
                      ? { usePlanner: true, planner: form.main, plannerKey: form.mainKey }
                      : { usePlanner: false, planner: form.main, plannerKey: '' });
                  }}
                />
                <span className="malpedia-settings-toggle-track" />
              </label>
            </div>
            {form.usePlanner && (
              <LlmFields
                cfg={form.planner}
                onChange={(planner) => update({ planner })}
                apiKey={form.plannerKey}
                onApiKeyChange={(plannerKey) => update({ plannerKey })}
                keyState={plannerKeyState}
                onClearKey={() => handleClearKey('plannerLlmApiKey')}
                backends={backends}
                locked={false}
              />
            )}
          </section>
        )}

        {!isTestAccount && (
          <div className={`malpedia-settings-savebar${dirty ? ' dirty' : ''}`}>
            <span className="malpedia-settings-savebar-status">
              {dirty ? 'Unsaved changes' : 'All changes saved'}
            </span>
            <button
              type="button"
              className="malpedia-settings-discard-btn"
              disabled={!dirty || saving}
              onClick={() => saved && setForm(saved)}
            >
              Discard
            </button>
            <button
              type="button"
              className="malpedia-settings-save-btn"
              disabled={!dirty || saving}
              onClick={handleSave}
            >
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        )}

        <ApiTokensSection />
      </div>
    </div>
  );
}
